<template>
    <div class="open-api-doc">
        <div class="doc-inner">
            <div v-if="loading" class="doc-tip">文档加载中…</div>
            <div v-else-if="error" class="doc-tip">文档加载失败,请稍后重试。</div>
            <div v-else class="markdown-body" v-html="html"></div>
        </div>
    </div>
</template>

<script>
    import {marked} from 'marked'
    import DOMPurify from 'dompurify'

    export default {
        name: 'open-api-doc',
        data() {
            return {html: '', loading: true, error: false}
        },
        created() {
            const url = (process.env.BASE_URL || '/') + 'docs/open-api.md'
            fetch(url)
                .then(res => {
                    if (!res.ok) throw new Error('HTTP ' + res.status)
                    return res.text()
                })
                .then(text => {
                    // dist/docs/open-api.md 缺失时,nginx 的 SPA history 回退会把请求转给
                    // index.html 并返回 HTTP 200,text 实际是整份 HTML。不挡住的话会被当成
                    // markdown 渲染出一坨乱码,而不是提示"加载失败"。
                    if (text.trimStart().startsWith('<')) throw new Error('not markdown')
                    this.html = DOMPurify.sanitize(marked.parse(text))
                    this.loading = false
                    this.$nextTick(this.mountCopyButtons)
                })
                .catch(() => {
                    this.error = true
                    this.loading = false
                })
        },
        beforeDestroy() {
            this.unmountCopyButtons()
        },
        methods: {
            // 文档内容是 v-html 注入的静态字符串,Vue 不会给里面的元素绑事件,
            // 只能在渲染完成后手动遍历 <pre> 挂按钮;组件销毁时要对称地摘掉监听,
            // 避免残留引用导致内存泄漏。
            mountCopyButtons() {
                const root = this.$el && this.$el.querySelector('.markdown-body')
                if (!root) return

                this._copyBtnHandlers = this._copyBtnHandlers || []
                root.querySelectorAll('pre').forEach(pre => {
                    if (pre.querySelector('.doc-copy-btn')) return

                    const btn = document.createElement('button')
                    btn.type = 'button'
                    btn.className = 'doc-copy-btn'
                    btn.textContent = '复制'

                    const handler = () => {
                        const codeEl = pre.querySelector('code') || pre
                        this.$copyText(codeEl.textContent).then(() => {
                            this.$notice.success('已经复制到剪贴板')
                        }).catch(() => {
                            this.$notice.error('复制失败,请手动复制')
                        })
                    }

                    btn.addEventListener('click', handler)
                    this._copyBtnHandlers.push({btn, handler})
                    pre.appendChild(btn)
                })
            },
            unmountCopyButtons() {
                (this._copyBtnHandlers || []).forEach(({btn, handler}) => {
                    btn.removeEventListener('click', handler)
                })
                this._copyBtnHandlers = []
            }
        }
    }
</script>

<style lang="scss">
    .open-api-doc {
        background: #fff;
        min-height: 100vh;
        padding: 32px 16px 64px;

        .doc-inner {
            max-width: 900px;
            margin: 0 auto;
        }

        .doc-tip {
            color: #909399;
            text-align: center;
            padding: 60px 0;
        }

        .markdown-body {
            color: #303133;
            line-height: 1.75;
            font-size: 14px;

            h1 { font-size: 26px; margin: 8px 0 24px; }
            h2 { font-size: 20px; margin: 32px 0 12px; padding-bottom: 8px; border-bottom: 1px solid #ebeef5; }
            h3 { font-size: 16px; margin: 24px 0 8px; }
            p { margin: 12px 0; }

            code {
                background: #f5f7fa;
                padding: 2px 6px;
                border-radius: 3px;
                font-family: Menlo, Consolas, monospace;
                font-size: 13px;
                color: #c7254e;
            }

            pre {
                position: relative;
                background: #2d2d2d;
                color: #ccc;
                padding: 16px;
                border-radius: 4px;
                overflow-x: auto;

                code {
                    background: transparent;
                    color: inherit;
                    padding: 0;
                }

                .doc-copy-btn {
                    position: absolute;
                    top: 8px;
                    right: 8px;
                    padding: 2px 10px;
                    font-size: 12px;
                    line-height: 20px;
                    color: #ccc;
                    background: rgba(255, 255, 255, 0.08);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    border-radius: 3px;
                    cursor: pointer;

                    &:hover {
                        color: #fff;
                        background: rgba(255, 255, 255, 0.18);
                    }
                }
            }

            table {
                border-collapse: collapse;
                width: 100%;
                margin: 16px 0;
                display: block;
                overflow-x: auto;

                th, td {
                    border: 1px solid #ebeef5;
                    padding: 8px 12px;
                    text-align: left;
                }

                th { background: #fafafa; font-weight: 600; }
            }

            blockquote {
                border-left: 3px solid #dcdfe6;
                padding-left: 12px;
                color: #909399;
                margin: 12px 0;
            }
        }
    }
</style>
